import { useState, useEffect, createContext, useContext } from 'react';
import { X, Cookie, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage, useLp } from '../context/LanguageContext';
import { Button } from './ui/button';

const CookieContext = createContext();

const cookieTranslations = {
  it: {
    title: 'Informativa sui Cookie',
    description: 'Utilizziamo cookie tecnici necessari per il funzionamento del sito e, previo consenso, cookie analitici per migliorare la tua esperienza. Puoi accettare tutti i cookie, rifiutare quelli non essenziali o personalizzare le tue preferenze.',
    acceptAll: 'Accetta Tutti',
    rejectAll: 'Rifiuta Non Essenziali',
    customize: 'Personalizza',
    save: 'Salva Preferenze',
    necessary: 'Cookie Necessari',
    necessaryDesc: 'Essenziali per il funzionamento del sito. Non possono essere disattivati.',
    analytics: 'Cookie Analitici',
    analyticsDesc: 'Ci aiutano a capire come utilizzi il sito per migliorarlo.',
    marketing: 'Cookie di Marketing',
    marketingDesc: 'Utilizzati per mostrarti pubblicità pertinenti.',
    moreInfo: 'Maggiori informazioni nella nostra',
    privacyPolicy: 'Privacy Policy',
    cookiePolicy: 'Cookie Policy'
  },
  en: {
    title: 'Cookie Notice',
    description: 'We use necessary technical cookies for the site to function and, with your consent, analytical cookies to improve your experience. You can accept all cookies, reject non-essential ones, or customize your preferences.',
    acceptAll: 'Accept All',
    rejectAll: 'Reject Non-Essential',
    customize: 'Customize',
    save: 'Save Preferences',
    necessary: 'Necessary Cookies',
    necessaryDesc: 'Essential for the site to function. Cannot be disabled.',
    analytics: 'Analytics Cookies',
    analyticsDesc: 'Help us understand how you use the site to improve it.',
    marketing: 'Marketing Cookies',
    marketingDesc: 'Used to show you relevant advertisements.',
    moreInfo: 'More information in our',
    privacyPolicy: 'Privacy Policy',
    cookiePolicy: 'Cookie Policy'
  },
  de: {
    title: 'Cookie-Hinweis',
    description: 'Wir verwenden notwendige technische Cookies für die Funktion der Website und, mit Ihrer Zustimmung, analytische Cookies zur Verbesserung Ihrer Erfahrung. Sie können alle Cookies akzeptieren, nicht wesentliche ablehnen oder Ihre Präferenzen anpassen.',
    acceptAll: 'Alle Akzeptieren',
    rejectAll: 'Nicht-Wesentliche Ablehnen',
    customize: 'Anpassen',
    save: 'Einstellungen Speichern',
    necessary: 'Notwendige Cookies',
    necessaryDesc: 'Wesentlich für die Funktion der Website. Können nicht deaktiviert werden.',
    analytics: 'Analyse-Cookies',
    analyticsDesc: 'Helfen uns zu verstehen, wie Sie die Website nutzen.',
    marketing: 'Marketing-Cookies',
    marketingDesc: 'Werden verwendet, um Ihnen relevante Werbung zu zeigen.',
    moreInfo: 'Weitere Informationen in unserer',
    privacyPolicy: 'Datenschutzerklärung',
    cookiePolicy: 'Cookie-Richtlinie'
  },
  fr: {
    title: 'Avis sur les Cookies',
    description: 'Nous utilisons des cookies techniques nécessaires au fonctionnement du site et, avec votre consentement, des cookies analytiques pour améliorer votre expérience. Vous pouvez accepter tous les cookies, refuser les non essentiels ou personnaliser vos préférences.',
    acceptAll: 'Tout Accepter',
    rejectAll: 'Refuser Non-Essentiels',
    customize: 'Personnaliser',
    save: 'Enregistrer les Préférences',
    necessary: 'Cookies Nécessaires',
    necessaryDesc: 'Essentiels au fonctionnement du site. Ne peuvent pas être désactivés.',
    analytics: 'Cookies Analytiques',
    analyticsDesc: 'Nous aident à comprendre comment vous utilisez le site.',
    marketing: 'Cookies Marketing',
    marketingDesc: 'Utilisés pour vous montrer des publicités pertinentes.',
    moreInfo: 'Plus d\'informations dans notre',
    privacyPolicy: 'Politique de Confidentialité',
    cookiePolicy: 'Politique des Cookies'
  }
};

export const CookieProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie_consent');
    if (savedConsent) {
      setConsent(JSON.parse(savedConsent));
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  const saveConsent = (newConsent) => {
    const consentData = {
      ...newConsent,
      timestamp: new Date().toISOString(),
      necessary: true // Always true
    };
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    setConsent(consentData);
    setShowBanner(false);
  };

  const revokeConsent = () => {
    localStorage.removeItem('cookie_consent');
    setConsent(null);
    setShowBanner(true);
  };

  return (
    <CookieContext.Provider value={{ consent, saveConsent, revokeConsent, showBanner, setShowBanner }}>
      {children}
    </CookieContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieProvider');
  }
  return context;
};

export const CookieBanner = () => {
  const { language } = useLanguage();
  const lp = useLp();
  const { showBanner, saveConsent } = useCookieConsent();
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  const t = cookieTranslations[language] || cookieTranslations.it;

  if (!showBanner) return null;

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const handleRejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  return (
    <div 
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t border-slate-200 shadow-2xl max-h-[85vh] overflow-y-auto"
    >
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 sm:py-6">
        {!showCustomize ? (
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 sm:gap-6">
            <div className="flex items-start gap-4 flex-1">
              <Cookie className="hidden sm:block w-8 h-8 text-[#C9A227] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-['Playfair_Display'] text-base sm:text-lg font-semibold text-[#283880] mb-1 sm:mb-2">
                  {t.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#64748B] leading-relaxed">
                  {t.description}{' '}
                  <Link to={lp('/privacy-policy')} className="text-[#C9A227] hover:underline">
                    {t.privacyPolicy}
                  </Link>{' & '}
                  <Link to={lp('/cookie-policy')} className="text-[#C9A227] hover:underline">
                    {t.cookiePolicy}
                  </Link>
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 sm:gap-3 w-full lg:w-auto [&>button]:h-11">
              <Button
                onClick={() => setShowCustomize(true)}
                data-testid="cookie-customize"
                variant="outline"
                className="border-slate-300 text-[#64748B] hover:bg-slate-50 text-xs uppercase tracking-wider"
              >
                {t.customize}
              </Button>
              <Button
                onClick={handleRejectAll}
                data-testid="cookie-reject"
                variant="outline"
                className="border-[#283880] text-[#283880] hover:bg-[#283880] hover:text-white text-xs uppercase tracking-wider"
              >
                {t.rejectAll}
              </Button>
              <Button
                onClick={handleAcceptAll}
                data-testid="cookie-accept"
                className="bg-[#C9A227] text-[#283880] hover:bg-[#B8911F] text-xs uppercase tracking-wider"
              >
                {t.acceptAll}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#283880]">
                {t.customize}
              </h3>
              <button 
                onClick={() => setShowCustomize(false)}
                className="text-[#64748B] hover:text-[#283880]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Necessary Cookies */}
              <div className="flex items-start justify-between p-4 bg-slate-50 border border-slate-200">
                <div>
                  <h4 className="font-medium text-[#283880]">{t.necessary}</h4>
                  <p className="text-sm text-[#64748B]">{t.necessaryDesc}</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={true} 
                  disabled 
                  className="w-5 h-5 accent-[#4A5FC1]"
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start justify-between p-4 bg-slate-50 border border-slate-200">
                <div>
                  <h4 className="font-medium text-[#283880]">{t.analytics}</h4>
                  <p className="text-sm text-[#64748B]">{t.analyticsDesc}</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                  data-testid="cookie-analytics-toggle"
                  className="w-5 h-5 accent-[#4A5FC1] cursor-pointer"
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start justify-between p-4 bg-slate-50 border border-slate-200">
                <div>
                  <h4 className="font-medium text-[#283880]">{t.marketing}</h4>
                  <p className="text-sm text-[#64748B]">{t.marketingDesc}</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                  data-testid="cookie-marketing-toggle"
                  className="w-5 h-5 accent-[#4A5FC1] cursor-pointer"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:flex sm:justify-end gap-2 sm:gap-3 [&>button]:h-11">
              <Button
                onClick={handleRejectAll}
                variant="outline"
                data-testid="cookie-customize-reject"
                className="border-slate-300 text-[#64748B]"
              >
                {t.rejectAll}
              </Button>
              <Button
                onClick={handleSavePreferences}
                data-testid="cookie-save"
                className="bg-[#C9A227] text-[#283880] hover:bg-[#B8911F]"
              >
                {t.save}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Small button to reopen cookie settings
export const CookieSettingsButton = () => {
  const { setShowBanner } = useCookieConsent();
  const { language } = useLanguage();
  
  return (
    <button
      onClick={() => setShowBanner(true)}
      data-testid="cookie-settings-btn"
      className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-xs"
      title={language === 'it' ? 'Gestisci Cookie' : language === 'de' ? 'Cookies Verwalten' : language === 'fr' ? 'Gérer les Cookies' : 'Manage Cookies'}
    >
      <Shield className="w-3 h-3" />
      <span>Cookie</span>
    </button>
  );
};
